import { useMutation } from '@tanstack/react-query';

import { isAPIError } from '@/utilities/errorHandler';
import { withAuth } from '@utilities/queries';

import { sentryCaptureMessage } from '@/components/Sentry';

import { getDownloadDetailedReturnsCSV } from '@simplywallst/services';
import { useSnackbar } from '@/features/snackbar';

type Args = {
  portfolioId: string;
  startDate: string;
  endDate: string;
};

export const useCSVDownload = () => {
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (args: Args) => {
      const response = await withAuth(getDownloadDetailedReturnsCSV, args);
      const { data } = response;
      return data;
    },
    onSuccess: (data, args) => {
      if (typeof data !== 'string') return;

      const { startDate, endDate } = args;
      const name =
        startDate.length === 0 && endDate.length === 0
          ? `detailed_returns_since_inception.csv`
          : `detailed_returns_${startDate}_${endDate}.csv`;

      try {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        a.remove();
      } catch (error) {
        sentryCaptureMessage({
          team: 'wombats',
          message: isAPIError(error) ? error.message : 'Unknown error',
          extra: {
            type: 'downloadCSV',
          },
        });
      }
    },
    onError: (error) => {
      sentryCaptureMessage({
        team: 'wombats',
        message: isAPIError(error) ? error.message : 'Unknown error',
        extra: {
          type: 'downloadCSV',
        },
      });

      addSnackbar({
        message: 'Failed to download CSV. Please try again.',
        type: 'negative',
        lifeTime: 2000,
      });
    },
  });
};
